import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

const schema = z
    .object({
        // Basic Details
        name: z.string().min(1, "Project name is required"),
        description: z.string().min(1, "Description is required"),
        type: z.string().min(1, "Project type is required"),
        // Autocomplete returns option objects; allow any, but require a value
        salesmanName: z.any().refine((val) => !!val, {
            message: "Salesman name is required",
        }),
        // Secondary salesman toggle + value (optional)
        hasSecondarySalesman: z.boolean().optional(),
        secondarySalesmanName: z.string().optional(),
        // Lead source is an object from LeadSourceDropdown
        leadSource: z.any().refine((val) => !!val, {
            message: "Lead source is required",
        }),
        secondaryLead: z.string().optional(),
        // Customer Details
        customerName: z.string().min(1, "Customer name is required"),
        customerPhoneNumber: z.string().min(1, "Customer phone number is required"),
        customerEmail: z.string().min(1, "Customer email is required").email("Invalid email address"),
        customerStreetAddress: z.string().min(1, "Customer street address is required"),
        customerCity: z.string().min(1, "Customer city is required"),
        customerState: z.string().min(1, "Customer state is required"),
        customerZipCode: z.string().min(1, "Customer ZIP code is required"),
        customerCountry: z.string().min(1, "Customer county is required"),
        // Property Details
        evorderid: z.string(),
        noEagleViewId: z.boolean(),
        copyAddressFromCustomer: z.boolean(),
        propertyStreetAddress: z.string().min(1, "Property street address is required"),
        propertyCity: z.string().min(1, "Property city is required"),
        propertyState: z.string().min(1, "Property state is required"),
        propertyZipCode: z.string().min(1, "Property ZIP code is required"),
        propertyCountry: z.string().min(1, "Property county is required"),
        // Date Estimation
        inspectionDate: z.string(),
        estimateDate: z.string(),
        permitSubmissionDate: z.string(),
        materialPurchaseDate: z.string(),
        jobStartDate: z.string(),
        estimatedEndDate: z.string(),
        // Insurance Details
        insuranceProvider: z.string(),
        policyNumber: z.string(),
        claimNumber: z.string(),
        adjusterContact: z.string(),
        adjusterEmail: z
            .string()
            .refine((val) => val === "" || z.string().email().safeParse(val).success, "Invalid email address"),
        noInsurance: z.boolean(),
        // Manual Measurements (when no EagleView ID)
        measurementArea: z.string(),
        measurementRidge: z.string(),
        measurementHip: z.string(),
        measurementValley: z.string(),
        measurementEave: z.string(),
        measurementRake: z.string(),
    })
    .refine(
        (data) => {
            // If noEagleViewId is false, require evorderid
            if (!data.noEagleViewId) {
                return data.evorderid && data.evorderid.length > 0;
            }
            return true;
        },
        {
            message: "EagleView Order ID is required when EagleView is enabled",
            path: ["evorderid"],
        }
    )
    .refine(
        (data) => {
            // If secondary salesman is enabled, require a value
            if (data.hasSecondarySalesman) {
                return !!data.secondarySalesmanName && data.secondarySalesmanName.trim().length > 0;
            }
            return true;
        },
        {
            message: "Secondary salesman name is required when enabled",
            path: ["secondarySalesmanName"],
        }
    )
    .refine(
        (data) => {
            // If noEagleViewId is true, require all measurement fields
            if (data.noEagleViewId) {
                return (
                    data.measurementArea &&
                    data.measurementArea.length > 0 &&
                    data.measurementRidge &&
                    data.measurementRidge.length > 0 &&
                    data.measurementHip &&
                    data.measurementHip.length > 0 &&
                    data.measurementValley &&
                    data.measurementValley.length > 0 &&
                    data.measurementEave &&
                    data.measurementEave.length > 0 &&
                    data.measurementRake &&
                    data.measurementRake.length > 0
                );
            }
            return true;
        },
        {
            message: "All measurement fields are required when EagleView is turned off",
            path: ["measurementArea"],
        }
    )
    .refine(
        (data) => {
            // If noInsurance is true, all insurance fields should be empty
            if (data.noInsurance) {
                return (
                    (!data.insuranceProvider || data.insuranceProvider.length === 0) &&
                    (!data.policyNumber || data.policyNumber.length === 0) &&
                    (!data.claimNumber || data.claimNumber.length === 0) &&
                    (!data.adjusterContact || data.adjusterContact.length === 0) &&
                    (!data.adjusterEmail || data.adjusterEmail.length === 0)
                );
            }
            return true;
        },
        {
            message: "Insurance fields must be empty when 'no insurance' is checked",
            path: ["insuranceProvider"],
        }
    );

const newProjectResolver = zodResolver(schema);

export default newProjectResolver;
